import { RoomContext, useDataChannel, useTracks } from "@livekit/components-react";
import React, { useContext, useState, useEffect } from "react";
import { DataPacket_Kind, LocalParticipant, Track } from "livekit-client";
import { ReceivedDataMessage } from "@livekit/components-core";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { UACService } from "../../services/uac.service";
import "./rdp.css";
import { Box } from "@mui/material";
import { getRtcToken } from "../../services/storage.service";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function RemoteAllow() {
  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const [showPopup, setShowPopup] = React.useState(false);
  const [msgFrom, setMsgFrom] = React.useState("");
  const [rdpStart, setRdpStart] = React.useState(false);
  const [remoteUserSid, setRemoteUserSid] = React.useState("");
  const [screenWidth, setScreenWidth] = React.useState(0);
  const [screenHeight, setScreenHeight] = React.useState(0);
  const [isNotUACBased, serIsNotUACBased] = useState(false);
  const room = useContext(RoomContext);
  const tracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    {}
  );
  const { message, send } = useDataChannel("RDP-channel", (msg) => action(msg));
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  

  const screenInfo = (
    data: { dimensions: { width: number; height: number } }
  ) => {
    setScreenWidth(data.dimensions.width);
    setScreenHeight(data.dimensions.height);
  };

  React.useEffect(() => {
    (window as any).electronAPI?.screenInfo().then((res: { dimensions: { width: number; height: number; }; }) => screenInfo(res));
  });

  const isUACBasedReq = async () => {
    const isAllowed = await UACService.checkCompatibility();
    if (!isAllowed) {
      serIsNotUACBased(true);
    }
  }

  const action = async (message: ReceivedDataMessage<"RDP-channel">) => {
    if (!message) return;
    const msg = decoder.decode(message.payload);
    let _msg = JSON.parse(msg);
    if (_msg.type === "message" && !rdpStart) {
      if (_msg.text === "controlAccessReq") {
        await isUACBasedReq();
        setShowPopup(true);
        setOpen(true);
        setMsgFrom(message.from?.name ?? "");
        setRemoteUserSid(message.from?.sid ?? "");
        new window.Notification(`${message.from?.name} wants remote access of your system`);
      } 
    }
    else if (_msg.type === "message" && _msg.text === "controlAccessEnd") {
      setRdpStart(false);
      let metadata;
      try { metadata = JSON.parse(room?.localParticipant.metadata ?? "") }
      catch { metadata = {}; }
      delete metadata['remoteAccessLock'];
      room?.localParticipant.setMetadata(JSON.stringify(metadata));
    }
    else if (_msg.message === "getRemoteAccessUser") {
      rdpStart && message?.from?.sid && send(encoder.encode(JSON.stringify({ message: "setRemoteAccessUser", text: msgFrom })), {
            kind: DataPacket_Kind.RELIABLE,
            destination: [message.from.sid],
          });
    }
    if (!rdpStart) return;

    switch (_msg.type) {
      case "mouseMove":
        (window as any).electronAPI?.moveMouse({
          x: _msg.x * screenWidth,
          y: _msg.y * screenHeight,
        });
        break;
      case "mouseDown":
        (window as any).electronAPI?.mouseToggleDown({
          x: _msg.x * screenWidth,
          y: _msg.y * screenHeight,
          button: _msg.button,
        });
        break;
      case "mouseUp":
        (window as any).electronAPI?.mouseToggleUp({
          x: _msg.x * screenWidth,
          y: _msg.y * screenHeight,
          button: _msg.button,
        });
        break;
      case "mouseScroll":
        (window as any).electronAPI?.mouseScroll({ delta: _msg.delta });
        break;
      case "keyDown":
          {
            (window as any).electronAPI?.keyPress({
              key: _msg.key,
              shift: _msg.shiftKey,
              ctrl: _msg.ctrlKey,
              alt: _msg.altKey,
              cmd: _msg.command,
            });
            if (_msg.ctrlKey && _msg.key === 67) {
              if (message.from) {
                const clipboardContent = await (window as any).electronAPI?.getClipboardText();
                const clipboardResponse = JSON.stringify({ type: "clipboardContent", content: clipboardContent });
                send(encoder.encode(clipboardResponse), {
                kind: DataPacket_Kind.RELIABLE,
                destination: [message.from.sid],
              });
              }
            }
            break;
          }
        case "clipboardContent":
          {
            await (window as any).electronAPI?.setClipboardText(_msg.content);
            await (window as any).electronAPI?.keyPress({
              key: 86,
              shift: null,
              ctrl: true,
              alt: null,
              cmd: null,
            });
          }
    }
  };

  const handlePopupAction = () => {
    setRdpStart(true);
    setShowPopup(false);
    if (remoteUserSid.length > 0)
      send(
        encoder.encode(
          JSON.stringify({ type: "message", text: "controlAccessGrant" })
        ),
        { kind: DataPacket_Kind.RELIABLE, destination: [remoteUserSid] }
      );
    let metadata;
    try { metadata = JSON.parse(room?.localParticipant.metadata ?? "") }
    catch { metadata = {}; }
    room?.localParticipant.setMetadata(JSON.stringify({...metadata, remoteAccessLock: true}));
    (window as any).electronAPI?.minimizeWindow();
  };

  React.useEffect(() => {
    if(tracks.length === 0 || !room?.participants.has(remoteUserSid) || !tracks.find((track) => track.publication?.source === Track.Source.ScreenShare && track.participant instanceof LocalParticipant)) {
      setRdpStart(false);
      let metadata;
      try { metadata = JSON.parse(room?.localParticipant.metadata ?? "") }
      catch { metadata = {}; }
      delete metadata['remoteAccessLock'];
      room?.state === "connected" && room?.localParticipant.setMetadata(JSON.stringify(metadata));
    }
  }, [tracks.length, room?.participants.size] )

  const handleRelaunchAsAdmin = () => {
    const token  = getRtcToken();
    !!(window as any).electronAPI && (window as any).electronAPI.restartAsAdmin({token: token});
  }

  return (
    <React.Fragment>
      {showPopup && (
        <div>
          <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            aria-describedby="alert-dialog-slide-description"
          >
            <DialogTitle>{"Grant remote access"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-slide-description">
                <b>
                  {msgFrom} wants remote access of your system, please confirm?
                </b>
                {isNotUACBased && (
                  <div className="uac-warning-box">
                    <strong>Warning:</strong> This session requires UAC-based access. Please run the application with administrator privileges to allow the remote user to control system-level features and applications.
                  </div>
                )}

              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
              <Box>
                {isNotUACBased && (
                  <Button
                    onClick={handleRelaunchAsAdmin}
                    variant="outlined"
                    color="warning"
                  >
                    Relaunch as System Admin
                  </Button>
                )}

              </Box>

              <Box>
                <Button style={{ marginRight: "10px" }} onClick={handleClose} variant="outlined">
                  Disagree
                </Button>
                <Button onClick={handlePopupAction} variant="contained" color="primary">
                  Agree
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </React.Fragment>
  );
}
